/**
 * masterSlice: DB 기반 마스터(호스트) 사용자 관리
 *
 * wehago-conference의 redux/modules/master.js에서 리팩토링.
 * Jitsi의 moderator role과는 별개로, 화상회의 서비스 자체 DB에서
 * API(/room/masters)를 통해 마스터 목록을 조회·관리한다.
 *
 * masterUsers 배열 아이템 구조: { user: string (wehagoId | externalUserId), ... }
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { MasterUser, RequestJoinUser } from '@/types/store';

// re-export for backwards compatibility
export type { MasterUser, RequestJoinUser } from '@/types/store';
export { isMasterUser } from '@/utils/participantUtils';

export interface MasterState {
  /** DB 기반 마스터 사용자 목록 */
  masterUsers: MasterUser[];
  /** 마스터 제어 대상 Jitsi ID */
  controlUserId: string | null;
  /** 마스터 컨트롤 여부 */
  isControl: boolean;
  /** 오디오 활성화 여부 */
  audioActive: boolean;
  /** 참여 요청 유저 리스트 (비공개 마스터방) */
  requestJoinUserList: RequestJoinUser[];
}

const initialState: MasterState = {
  masterUsers: [],
  controlUserId: null,
  isControl: false,
  audioActive: true,
  requestJoinUserList: [],
};

const masterSlice = createSlice({
  name: 'master',
  initialState,
  reducers: {
    setMasterUsers(state, action: PayloadAction<MasterUser[]>) {
      state.masterUsers = action.payload;
    },

    setControlUser(state, action: PayloadAction<{ userId: string | null; isLocal?: boolean }>) {
      state.controlUserId = action.payload.userId;
      state.isControl = !!action.payload.userId;
    },

    setIsControl(state, action: PayloadAction<boolean>) {
      state.isControl = action.payload;
    },

    setAudioActive(state, action: PayloadAction<boolean>) {
      state.audioActive = action.payload;
    },

    /** 참여 요청 유저 추가 (user_identify 기준 중복 체크) — v1 applyAddRequestJoinUser */
    addRequestJoinUser(state, action: PayloadAction<RequestJoinUser>) {
      const user = action.payload;
      const exists = state.requestJoinUserList.some(
        (u) => u.user_identify === user.user_identify,
      );
      if (!exists) {
        state.requestJoinUserList.push({ ...user, hasViewed: false });
      }
    },

    /** 참여 요청 유저 제거 (user_identify 기반) */
    removeRequestJoinUser(state, action: PayloadAction<{ user_identify: string }[]>) {
      const removeSet = new Set(action.payload.map((u) => u.user_identify));
      state.requestJoinUserList = state.requestJoinUserList.filter(
        (u) => !removeSet.has(u.user_identify),
      );
    },

    /** 참여 요청 유저 리스트 전체 설정 */
    setRequestJoinUsers(state, action: PayloadAction<RequestJoinUser[]>) {
      state.requestJoinUserList = action.payload;
    },

    reset() {
      return initialState;
    },
  },
});

export const masterActions = masterSlice.actions;
export default masterSlice.reducer;
